import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {Link, useNavigate} from 'react-router-dom';
import {Box, Container, Typography, TextField, Button, Alert, CircularProgress, InputAdornment, IconButton} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import UserCreate from '../../graphql/mutations/UserCreate';
import { useTranslation } from 'react-i18next';
import { LIBRARY, accountFromSession, signIn } from '../../helpers/accounts';
import Note from "./Note";

const SignUp = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleSignUp = () => {
        setIsLoading(true);
        try {
            UserCreate.commit({ firstName, lastName, email, password }).then(({ userCreate }) => {
                const { token, error } = userCreate || {};

                if (token) {
                    // Through the same door as signing in, so the tab rebinds to
                    // the new account rather than seeing it as a stranger's.
                    signIn(LIBRARY, { ...accountFromSession(userCreate.user), token });
                    navigate('/catalogue'); // Redirect to home page
                    window.location.reload(true);
                } else {
                    setError(error || t('somethingWentWrong', 'Something went wrong'));
                }
                setIsLoading(false);
            });
        } catch (err) {
            setIsLoading(false);
            console.error('Sign-up error:', err);
            toast.error(t('unexpectedError', 'An unexpected error occurred.'));
        }
    };

    return (
        <Container maxWidth="lg">
            <Box
                sx={{
                    maxWidth: '400px',
                    margin: '50px auto',
                    padding: 4,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: 'primary.main',
                    }}
                >
                    {t('signUp', 'Sign Up')}
                </Typography>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box component="form" noValidate autoComplete="off">
                    <TextField
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                        autoComplete="off"
                        label={t('firstName', 'First Name')}
                        variant="outlined"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                        autoComplete="off"
                        label={t('lastName', 'Last Name')}
                        variant="outlined"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                        autoComplete="off"
                        type="email"
                        label={t('email', 'Email')}
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                        autoComplete="off"
                        label={t('password', 'Password')}
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 3 }}
                    />
                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        disabled={isLoading || password.length === 0 || email.length === 0 || lastName.length === 0 || firstName.length === 0}
                        onClick={handleSignUp}
                        sx={{
                            fontSize: '1rem',
                            padding: '12px 0',
                            textTransform: 'none'
                        }}
                    >
                        {isLoading && <CircularProgress size={20} color="inherit" sx={{ marginRight: '15px' }} />}
                        {t('signUp', 'Sign Up')}
                    </Button>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2,
                    }}
                >
                    <Typography variant="body2">
                        <Link
                            to="/sign-in"
                            style={{ color: '#80a748', textDecoration: 'none' }}
                        >
                            {t('signIn', 'Sign In')}
                        </Link>
                    </Typography>

                    <Typography variant="body2">
                        <Link
                            to="/forgot-password"
                            style={{ color: '#80a748', textDecoration: 'none' }}
                        >
                            {t('forgotPassword', 'Forgot password?')}
                        </Link>
                    </Typography>
                </Box>
                <Note i18nKey="signUpNoteRich" />
            </Box>
        </Container>
    );
};

export default SignUp;
